import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { i18next } from '@frontend/i18n';

dayjs.extend(duration);

/**
 * DS guidelines:
 * Same day – Today 00:00 AM
 * Previous day – Yesterday 00:00 AM
 * All days before yesterday – April 1, 00:00 AM
 * Outside current year – April 1, 2023, 00:00 AM
 */
export const humanizeDateTime = (dateTime: string, skipTimeInDates?: boolean) => {
  const today = dayjs();
  const date = dayjs(dateTime);

  // Today
  const isToday = today.isSame(date, 'day');
  if (isToday) {
    return i18next.t('Today {{time}}', { time: date.format('h:mm A') });
  }

  // Yesterday
  const yesterday = today.subtract(1, 'day');
  const isYesterday = yesterday.isSame(date, 'day');
  if (isYesterday) {
    return i18next.t('Yesterday {{time}}', { time: date.format('h:mm A') });
  }

  // All days before yesterday (within this year)
  if (today.year() === date.year()) {
    return skipTimeInDates ? date.format('MMM DD') : date.format('MMM DD, h:mm A');
  }

  // Outside current year
  return skipTimeInDates ? date.format('MMM DD, YYYY') : date.format('MMM DD, YYYY, h:mm A');
};

export const humanizeDateDifference = (startDate: string, endDate: string, withoutLabel = false) => {
  if (!startDate || !endDate) {
    return '';
  }

  const isSameDay = dayjs(startDate).isSame(dayjs(endDate), 'day');
  if (isSameDay) {
    if (withoutLabel) {
      return `${dayjs(startDate).format('MMM D')}`;
    }

    if (dayjs(startDate).isSame(dayjs(), 'day')) {
      return i18next.t('Today');
    } else if (dayjs(startDate).isSame(dayjs().add(1, 'day'), 'day')) {
      return i18next.t('Tomorrow');
    } else if (dayjs(startDate).isSame(dayjs().subtract(1, 'day'), 'day')) {
      return i18next.t('Yesterday');
    } else if (dayjs(startDate).isSame(dayjs(), 'year')) {
      return dayjs(startDate).format('MMM D');
    }
  }

  const isSameYear = dayjs(startDate).isSame(dayjs(endDate), 'year');
  if (isSameYear) {
    return `${dayjs(startDate).format('MMM D')} - ${dayjs(endDate).format('MMM D')}`;
  }

  return `${dayjs(startDate).format('MMM D, YYYY')} - ${dayjs(endDate).format('MMM D, YYYY')}`;
};

export const humanizeSeconds = (durationInSeconds?: number) => {
  let value = '';
  const duration = dayjs.duration(durationInSeconds || 0, 'seconds');
  const hours = duration.format('H');
  const mins = duration.format('m');
  const secs = duration.format('s');

  if (hours !== '0') {
    value += `${hours}hour `;
  }

  if (mins !== '0') {
    value += `${mins}min `;
  }

  if (secs !== '0') {
    value += `${secs}sec`;
  }
  return value || '0sec';
};
